import { Badge, Button, EmptyState, type Tone } from "@coucou/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { formatDay } from "../lib/download";
import { SALON_ID, api } from "../lib/trpc";

type Kind = "training_school" | "supplier" | "brand" | "local_business" | "influencer" | "other";
type Status = "prospect" | "contacted" | "in_discussion" | "active" | "paused" | "ended";
type CandidateStatus = "received" | "interview" | "trial_day" | "offer" | "hired" | "rejected" | "withdrawn";
type Tab = "partners" | "candidates";

const KINDS: { key: Kind; label: string; glyph: string }[] = [
  { key: "training_school", label: "Écoles & CFA", glyph: "🎓" },
  { key: "supplier", label: "Fournisseurs", glyph: "📦" },
  { key: "brand", label: "Marques", glyph: "✨" },
  { key: "local_business", label: "Commerces voisins", glyph: "🏪" },
  { key: "influencer", label: "Créatrices", glyph: "📸" },
  { key: "other", label: "Autres", glyph: "🤝" },
];
const kindMeta = (kind: string) => KINDS.find((entry) => entry.key === kind) ?? KINDS[5]!;

const STATUSES: { key: Status; label: string; tone: Tone }[] = [
  { key: "prospect", label: "À contacter", tone: "neutral" },
  { key: "contacted", label: "Contacté", tone: "neutral" },
  { key: "in_discussion", label: "En discussion", tone: "warning" },
  { key: "active", label: "Partenariat actif", tone: "success" },
  { key: "paused", label: "En pause", tone: "muted" },
  { key: "ended", label: "Terminé", tone: "muted" },
];
const statusMeta = (status: string) => STATUSES.find((entry) => entry.key === status) ?? STATUSES[0]!;

/** Le pipeline de recrutement, dans l'ordre où une candidate le traverse. */
const PIPELINE: { key: CandidateStatus; label: string }[] = [
  { key: "received", label: "Reçues" },
  { key: "interview", label: "Entretien" },
  { key: "trial_day", label: "Journée d'essai" },
  { key: "offer", label: "Proposition" },
  { key: "hired", label: "Recrutées" },
];
const CLOSED: { key: CandidateStatus; label: string }[] = [
  { key: "rejected", label: "Non retenue" },
  { key: "withdrawn", label: "Désistement" },
];
const candidateLabel = (status: string) =>
  [...PIPELINE, ...CLOSED].find((entry) => entry.key === status)?.label ?? status;

const INTERACTIONS = [
  { key: "call", label: "Appel", glyph: "📞" },
  { key: "email", label: "E-mail", glyph: "✉️" },
  { key: "meeting", label: "Rendez-vous", glyph: "☕" },
  { key: "event", label: "Événement", glyph: "🎪" },
  { key: "note", label: "Note", glyph: "📝" },
] as const;

const CONTRACTS = [
  { key: "apprentissage", label: "Apprentissage" },
  { key: "professionnalisation", label: "Contrat pro" },
  { key: "stage", label: "Stage" },
  { key: "cdd", label: "CDD" },
  { key: "cdi", label: "CDI" },
] as const;

const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const MONTHS_LONG = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

const todayIso = () => new Date().toISOString().slice(0, 10);
const inDays = (days: number) => new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10);

const inputClass = "h-10 w-full rounded-lg border border-creme-300 bg-white px-3";

/**
 * Partenariats.
 *
 * Pensé pour une gérante qui recrute : un écran qui dit QUI rappeler cette
 * semaine, QUELLE école fait sa rentrée bientôt, et OÙ en sont les candidates.
 * Les fiches viennent après — on ouvre l'écran pour agir, pas pour consulter
 * un annuaire.
 */
export function Partners() {
  const [tab, setTab] = useState<Tab>("partners");
  const [openId, setOpenId] = useState<string | "new" | null>(null);
  const [candidate, setCandidate] = useState<CandidateDraft | null>(null);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="shrink-0 px-6 pb-2 pt-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl">Partenariats</h1>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setCandidate(emptyCandidate())}>
              + Candidature
            </Button>
            <Button onClick={() => setOpenId("new")}>+ Partenaire</Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          {(
            [
              { key: "partners", label: "Partenaires" },
              { key: "candidates", label: "Candidatures" },
            ] as const
          ).map((entry) => (
            <button
              key={entry.key}
              type="button"
              onClick={() => setTab(entry.key)}
              aria-current={tab === entry.key ? "page" : undefined}
              className={[
                "min-h-9 rounded-full px-4 text-sm transition-colors",
                tab === entry.key ? "bg-blush-500 font-medium text-white" : "text-encre-600 hover:bg-creme-200",
              ].join(" ")}
            >
              {entry.label}
            </button>
          ))}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-auto px-6 pb-8 pt-4">
        <Overview onOpen={setOpenId} onTab={setTab} />
        {tab === "partners" && <PartnerList onOpen={setOpenId} />}
        {tab === "candidates" && <CandidateBoard onEdit={setCandidate} />}
      </div>

      {openId && (
        <PartnerDrawer
          id={openId}
          onClose={() => setOpenId(null)}
          onCreated={setOpenId}
          onCandidate={setCandidate}
        />
      )}
      {candidate && <CandidateDialog draft={candidate} onClose={() => setCandidate(null)} />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  En-tête : ce qui demande une action                                        */
/* -------------------------------------------------------------------------- */

function Overview({ onOpen, onTab }: { onOpen: (id: string) => void; onTab: (tab: Tab) => void }) {
  const overview = useQuery({
    queryKey: ["partners", "overview"],
    queryFn: () => api.admin.partners.overview.query({ salonId: SALON_ID }),
  });
  const data = overview.data;
  if (!data) return null;

  const enCours = PIPELINE.filter((step) => step.key !== "hired").reduce(
    (sum, step) => sum + (data.candidatesByStatus[step.key] ?? 0),
    0,
  );

  return (
    <div className="mb-6 grid gap-3 lg:grid-cols-3">
      <div className="rounded-xl bg-white p-4 shadow-soft">
        <p className="text-xs uppercase tracking-wide text-encre-400">À relancer cette semaine</p>
        {data.dueFollowUps.length === 0 ? (
          <p className="mt-2 text-sm text-encre-500">Rien d'urgent. 🌿</p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {data.dueFollowUps.map((due) => {
              const enRetard = due.nextFollowUpOn! < data.today;
              return (
                <li key={due.id}>
                  <button
                    type="button"
                    onClick={() => onOpen(due.id)}
                    className="flex w-full items-center justify-between gap-2 text-left text-sm hover:text-blush-600"
                  >
                    <span className="truncate">
                      {kindMeta(due.kind).glyph} {due.name}
                    </span>
                    <span className={["shrink-0 tabular-nums", enRetard ? "font-medium text-grenat-700" : "text-encre-500"].join(" ")}>
                      {due.nextFollowUpOn === data.today ? "aujourd'hui" : formatDay(due.nextFollowUpOn)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="rounded-xl bg-white p-4 shadow-soft">
        <p className="text-xs uppercase tracking-wide text-encre-400">Rentrées à venir</p>
        {data.upcomingIntakes.length === 0 ? (
          <p className="mt-2 text-sm text-encre-500">
            {data.knowsIntakes
              ? "Aucune rentrée dans les quatre prochains mois."
              : "Renseignez les mois de rentrée de vos écoles : l'écran vous dira quand les appeler."}
          </p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {data.upcomingIntakes.slice(0, 6).map((intake) => (
              <li key={`${intake.partnerId}-${intake.month}`}>
                <button
                  type="button"
                  onClick={() => onOpen(intake.partnerId)}
                  className="flex w-full items-center justify-between gap-2 text-left text-sm hover:text-blush-600"
                >
                  <span className="truncate">🎓 {intake.name}</span>
                  <span className="shrink-0 text-encre-500">
                    {intake.inMonths === 0 ? "ce mois-ci" : `en ${MONTHS_LONG[intake.month - 1]}`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <button
        type="button"
        onClick={() => onTab("candidates")}
        className="rounded-xl bg-white p-4 text-left shadow-soft transition-shadow hover:shadow-lift"
      >
        <p className="text-xs uppercase tracking-wide text-encre-400">Recrutement</p>
        <p className="mt-1 font-display text-2xl text-encre-900" data-numeric>
          {enCours} candidature{enCours > 1 ? "s" : ""} en cours
        </p>
        <p className="text-sm text-encre-500">
          {data.candidatesByStatus.hired ?? 0} recrutée{(data.candidatesByStatus.hired ?? 0) > 1 ? "s" : ""} ·{" "}
          {data.byStatus.active ?? 0} partenariat{(data.byStatus.active ?? 0) > 1 ? "s" : ""} actif
          {(data.byStatus.active ?? 0) > 1 ? "s" : ""}
        </p>
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Liste des partenaires                                                      */
/* -------------------------------------------------------------------------- */

function PartnerList({ onOpen }: { onOpen: (id: string) => void }) {
  const [kind, setKind] = useState<Kind | null>(null);
  const [status, setStatus] = useState<Status | "">("");
  const [search, setSearch] = useState("");
  const [archived, setArchived] = useState(false);

  const list = useQuery({
    queryKey: ["partners", "list", kind, status, search.trim(), archived],
    queryFn: () =>
      api.admin.partners.list.query({
        salonId: SALON_ID,
        kind: kind ?? undefined,
        status: status || undefined,
        search: search.trim() || undefined,
        archived,
      }),
  });

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setKind(null)}
          aria-pressed={kind === null}
          className={chip(kind === null)}
        >
          Tous
        </button>
        {KINDS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            onClick={() => setKind(entry.key)}
            aria-pressed={kind === entry.key}
            className={chip(kind === entry.key)}
          >
            {entry.glyph} {entry.label}
          </button>
        ))}
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Nom, ville…"
          aria-label="Rechercher un partenaire"
          className="h-10 w-full max-w-xs rounded-lg border border-creme-300 bg-white px-3"
        />
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value as Status | "")}
          aria-label="Filtrer par statut"
          className="h-10 rounded-lg border border-creme-300 bg-white px-3 text-sm"
        >
          <option value="">Tous les statuts</option>
          {STATUSES.map((entry) => (
            <option key={entry.key} value={entry.key}>
              {entry.label}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-encre-600">
          <input type="checkbox" checked={archived} onChange={(event) => setArchived(event.target.checked)} />
          Archivés
        </label>
      </div>

      {list.isPending && <p className="text-encre-400">Chargement…</p>}

      {list.data?.length === 0 && (
        <EmptyState
          glyph="🎓"
          title={kind || status || search ? "Aucun partenaire ne correspond" : "Aucun partenaire pour l'instant"}
          description={
            kind || status || search
              ? "Retirez un filtre pour élargir la recherche."
              : "Commencez par les écoles d'esthétique et les CFA de la région : ce sont elles qui vous enverront vos futures alternantes."
          }
        />
      )}

      <ul className="space-y-2">
        {list.data?.map((partner) => {
          const meta = statusMeta(partner.status);
          const enRetard = partner.nextFollowUpOn && partner.nextFollowUpOn < todayIso();
          return (
            <li key={partner.id}>
              <button
                type="button"
                onClick={() => onOpen(partner.id)}
                className="flex w-full flex-wrap items-center gap-4 rounded-xl bg-white p-4 text-left shadow-soft transition-shadow hover:shadow-lift"
              >
                <span aria-hidden className="text-2xl">
                  {kindMeta(partner.kind).glyph}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-lg leading-tight">
                    {partner.name}
                    {partner.city && <span className="ml-2 text-sm font-normal text-encre-400">{partner.city}</span>}
                  </p>
                  <p className="truncate text-sm text-encre-500">
                    {partner.summary ?? kindMeta(partner.kind).label}
                    {partner.primaryContact && ` · ${partner.primaryContact}`}
                  </p>
                </div>
                {partner.kind === "training_school" && partner.candidateCount > 0 && (
                  <span className="text-sm text-encre-500">
                    {partner.candidateCount} candidature{partner.candidateCount > 1 ? "s" : ""}
                    {partner.hiredCount > 0 && ` · ${partner.hiredCount} recrutée${partner.hiredCount > 1 ? "s" : ""}`}
                  </span>
                )}
                {partner.nextFollowUpOn && (
                  <span className={["text-sm", enRetard ? "font-medium text-grenat-700" : "text-encre-500"].join(" ")}>
                    Relance {formatDay(partner.nextFollowUpOn)}
                  </span>
                )}
                <Badge tone={meta.tone} dot>
                  {meta.label}
                </Badge>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const chip = (active: boolean) =>
  [
    "min-h-9 rounded-full px-3.5 text-sm transition-colors",
    active ? "bg-encre-800 text-white" : "bg-creme-100 text-encre-700 hover:bg-creme-200",
  ].join(" ");

/* -------------------------------------------------------------------------- */
/*  Fiche partenaire                                                           */
/* -------------------------------------------------------------------------- */

type PartnerDraft = {
  kind: Kind;
  status: Status;
  name: string;
  website: string;
  email: string;
  phone: string;
  city: string;
  address: string;
  summary: string;
  notes: string;
  programs: string;
  intakeMonths: number[];
  rhythm: string;
  contractTypes: Array<"apprentissage" | "professionnalisation" | "stage">;
  opco: string;
  studentsPerYear: string;
  agreementStartsOn: string;
  agreementEndsOn: string;
};

const EMPTY_PARTNER: PartnerDraft = {
  kind: "training_school",
  status: "prospect",
  name: "",
  website: "",
  email: "",
  phone: "",
  city: "",
  address: "",
  summary: "",
  notes: "",
  programs: "",
  intakeMonths: [],
  rhythm: "",
  contractTypes: [],
  opco: "",
  studentsPerYear: "",
  agreementStartsOn: "",
  agreementEndsOn: "",
};

function PartnerDrawer({
  id,
  onClose,
  onCreated,
  onCandidate,
}: {
  id: string | "new";
  onClose: () => void;
  onCreated: (id: string) => void;
  onCandidate: (draft: CandidateDraft) => void;
}) {
  const queryClient = useQueryClient();
  const isNew = id === "new";
  const [editing, setEditing] = useState(isNew);

  const detail = useQuery({
    queryKey: ["partners", "detail", id],
    queryFn: () => api.admin.partners.detail.query({ id }),
    enabled: !isNew,
  });

  useEffect(() => setEditing(isNew), [id, isNew]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["partners"] });

  const archive = useMutation({
    mutationFn: (archived: boolean) => api.admin.partners.archive.mutate({ id, archived }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const partner = detail.data;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Partenaire"
      className="fixed inset-0 z-50 flex justify-end bg-encre-900/30 animate-fade-in"
      onClick={onClose}
    >
      <aside
        className="h-full w-full max-w-2xl overflow-y-auto bg-white p-6 shadow-lift"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl">{isNew ? "Nouveau partenaire" : (partner?.name ?? "…")}</h2>
            {partner && (
              <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-encre-500">
                {kindMeta(partner.kind).glyph} {kindMeta(partner.kind).label}
                <Badge tone={statusMeta(partner.status).tone} dot>
                  {statusMeta(partner.status).label}
                </Badge>
                {partner.archivedAt && <Badge tone="muted">Archivé</Badge>}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="rounded-full px-2 text-encre-400 hover:bg-creme-100"
          >
            ×
          </button>
        </div>

        {editing ? (
          <PartnerForm
            partner={partner}
            onCancel={() => (isNew ? onClose() : setEditing(false))}
            onSaved={(saved) => {
              invalidate();
              if (isNew) onCreated(saved.id);
              setEditing(false);
            }}
          />
        ) : (
          partner && (
            <>
              <PartnerSummary partner={partner} onEdit={() => setEditing(true)} />
              <Interactions partnerId={partner.id} status={partner.status} interactions={partner.interactions} />
              <Contacts partnerId={partner.id} contacts={partner.contacts} />

              {(partner.kind === "training_school" || partner.candidates.length > 0) && (
                <section className="mt-8">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h3 className="text-lg">Candidatures venues de {partner.name}</h3>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => onCandidate({ ...emptyCandidate(), partnerId: partner.id })}
                    >
                      + Candidature
                    </Button>
                  </div>
                  {partner.candidates.length === 0 ? (
                    <p className="text-sm text-encre-400">Aucune pour l'instant.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {partner.candidates.map((entry) => (
                        <li key={entry.id}>
                          <button
                            type="button"
                            onClick={() => onCandidate(toCandidateDraft(entry))}
                            className="flex w-full items-center justify-between gap-2 rounded-lg bg-creme-50 px-3 py-2 text-left text-sm hover:bg-creme-100"
                          >
                            <span>
                              {entry.firstName} {entry.lastName}
                              {entry.program && <span className="text-encre-400"> · {entry.program}</span>}
                            </span>
                            <Badge tone={entry.status === "hired" ? "success" : "neutral"}>
                              {candidateLabel(entry.status)}
                            </Badge>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              )}

              <div className="mt-10 border-t border-creme-200 pt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  loading={archive.isPending}
                  onClick={() => archive.mutate(!partner.archivedAt)}
                >
                  {partner.archivedAt ? "Ressortir des archives" : "Archiver ce partenaire"}
                </Button>
              </div>
            </>
          )
        )}
      </aside>
    </div>
  );
}

type PartnerDetail = Awaited<ReturnType<typeof api.admin.partners.detail.query>>;

function PartnerSummary({ partner, onEdit }: { partner: PartnerDetail; onEdit: () => void }) {
  const d = partner.details;
  const lignes: [string, React.ReactNode][] = [];
  if (partner.summary) lignes.push(["En bref", partner.summary]);
  if (partner.phone) lignes.push(["Téléphone", <a className="hover:underline" href={`tel:${partner.phone}`}>{partner.phone}</a>]);
  if (partner.email) lignes.push(["E-mail", <a className="hover:underline" href={`mailto:${partner.email}`}>{partner.email}</a>]);
  if (partner.website)
    lignes.push([
      "Site",
      <a className="break-all hover:underline" href={partner.website} target="_blank" rel="noreferrer">
        {partner.website.replace(/^https?:\/\//, "")}
      </a>,
    ]);
  if (partner.address || partner.city) lignes.push(["Adresse", [partner.address, partner.city].filter(Boolean).join(", ")]);
  if (partner.kind === "training_school") {
    if (d.programs?.length) lignes.push(["Formations", d.programs.join(" · ")]);
    if (d.intakeMonths?.length)
      lignes.push(["Rentrées", d.intakeMonths.slice().sort((a, b) => a - b).map((m) => MONTHS_LONG[m - 1]).join(", ")]);
    if (d.rhythm) lignes.push(["Rythme", d.rhythm]);
    if (d.contractTypes?.length)
      lignes.push(["Contrats", d.contractTypes.map((c) => CONTRACTS.find((x) => x.key === c)?.label ?? c).join(", ")]);
    if (d.opco) lignes.push(["OPCO", d.opco]);
    if (d.studentsPerYear) lignes.push(["Élèves / an", String(d.studentsPerYear)]);
  }
  if (partner.agreementStartsOn || partner.agreementEndsOn)
    lignes.push([
      "Convention",
      `${partner.agreementStartsOn ? `du ${formatDay(partner.agreementStartsOn)}` : ""} ${partner.agreementEndsOn ? `au ${formatDay(partner.agreementEndsOn)}` : ""}`.trim(),
    ]);

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-lg">Fiche</h3>
        <Button size="sm" variant="secondary" onClick={onEdit}>
          Modifier
        </Button>
      </div>
      {lignes.length === 0 ? (
        <p className="text-sm text-encre-400">Aucune information pour l'instant.</p>
      ) : (
        <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-2 text-sm">
          {lignes.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-encre-400">{label}</dt>
              <dd className="text-encre-800">{value}</dd>
            </div>
          ))}
        </dl>
      )}
      {partner.notes && (
        <p className="mt-3 whitespace-pre-wrap rounded-md bg-creme-100 px-3 py-2 text-sm text-encre-600">{partner.notes}</p>
      )}
    </section>
  );
}

function PartnerForm({
  partner,
  onSaved,
  onCancel,
}: {
  partner?: PartnerDetail;
  onSaved: (saved: { id: string }) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<PartnerDraft>(() =>
    partner
      ? {
          kind: partner.kind,
          status: partner.status,
          name: partner.name,
          website: partner.website ?? "",
          email: partner.email ?? "",
          phone: partner.phone ?? "",
          city: partner.city ?? "",
          address: partner.address ?? "",
          summary: partner.summary ?? "",
          notes: partner.notes ?? "",
          programs: (partner.details.programs ?? []).join(", "),
          intakeMonths: partner.details.intakeMonths ?? [],
          rhythm: partner.details.rhythm ?? "",
          contractTypes: partner.details.contractTypes ?? [],
          opco: partner.details.opco ?? "",
          studentsPerYear: partner.details.studentsPerYear ? String(partner.details.studentsPerYear) : "",
          agreementStartsOn: partner.agreementStartsOn ?? "",
          agreementEndsOn: partner.agreementEndsOn ?? "",
        }
      : EMPTY_PARTNER,
  );
  const set = <K extends keyof PartnerDraft>(key: K, value: PartnerDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const text = (key: keyof PartnerDraft) => ({
    value: draft[key] as string,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => set(key, event.target.value as never),
  });

  const save = useMutation({
    mutationFn: () =>
      api.admin.partners.save.mutate({
        id: partner?.id,
        salonId: SALON_ID,
        kind: draft.kind,
        status: draft.status,
        name: draft.name,
        website: draft.website,
        email: draft.email,
        phone: draft.phone,
        city: draft.city,
        address: draft.address,
        summary: draft.summary,
        notes: draft.notes,
        tags: partner?.tags ?? [],
        details:
          draft.kind === "training_school"
            ? {
                programs: draft.programs.split(",").map((p) => p.trim()).filter(Boolean),
                intakeMonths: draft.intakeMonths,
                rhythm: draft.rhythm.trim() || undefined,
                contractTypes: draft.contractTypes,
                opco: draft.opco.trim() || undefined,
                studentsPerYear: draft.studentsPerYear ? Number(draft.studentsPerYear) : undefined,
              }
            : {},
        agreementStartsOn: draft.agreementStartsOn || null,
        agreementEndsOn: draft.agreementEndsOn || null,
      }),
    onSuccess: onSaved,
  });

  const school = draft.kind === "training_school";

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <fieldset>
        <legend className="mb-2 text-sm font-medium text-encre-700">Type de partenaire</legend>
        <div className="flex flex-wrap gap-2">
          {KINDS.map((entry) => (
            <button
              key={entry.key}
              type="button"
              onClick={() => set("kind", entry.key)}
              aria-pressed={draft.kind === entry.key}
              className={[
                "min-h-10 rounded-full px-3.5 text-sm transition-colors",
                draft.kind === entry.key ? "bg-blush-500 text-white" : "bg-creme-100 text-encre-700 hover:bg-creme-200",
              ].join(" ")}
            >
              {entry.glyph} {entry.label}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Nom" className="sm:col-span-2">
          <input {...text("name")} required maxLength={160} className={inputClass} placeholder={school ? "CFA des métiers de la beauté" : ""} />
        </Field>
        <Field label="Statut">
          <select value={draft.status} onChange={(event) => set("status", event.target.value as Status)} className={inputClass}>
            {STATUSES.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Ville">
          <input {...text("city")} maxLength={120} className={inputClass} />
        </Field>
        <Field label="En une phrase (facultatif)" className="sm:col-span-2">
          <input
            {...text("summary")}
            maxLength={300}
            className={inputClass}
            placeholder={school ? "Envoie 2 à 3 alternantes CAP par an" : "Remise de 15 % sur les gels"}
          />
        </Field>
        <Field label="Téléphone">
          <input {...text("phone")} type="tel" maxLength={32} className={inputClass} />
        </Field>
        <Field label="E-mail">
          <input {...text("email")} type="email" maxLength={255} className={inputClass} />
        </Field>
        <Field label="Site web">
          <input {...text("website")} type="url" maxLength={255} className={inputClass} placeholder="https://" />
        </Field>
        <Field label="Adresse">
          <input {...text("address")} maxLength={255} className={inputClass} />
        </Field>
      </div>

      {/* Ce qui distingue une école d'un fournisseur : son calendrier. */}
      {school && (
        <fieldset className="rounded-xl bg-creme-50 p-4">
          <legend className="px-1 text-sm font-medium text-encre-700">🎓 L'école et ses élèves</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Formations (séparées par des virgules)" className="sm:col-span-2">
              <input
                {...text("programs")}
                className={inputClass}
                placeholder="CAP Esthétique, BP Esthétique, Prothésiste ongulaire"
              />
            </Field>
            <div className="sm:col-span-2">
              <p className="mb-1.5 text-sm font-medium text-encre-700">Mois de rentrée</p>
              <div className="flex flex-wrap gap-1.5">
                {MONTHS.map((label, index) => {
                  const month = index + 1;
                  const on = draft.intakeMonths.includes(month);
                  return (
                    <button
                      key={label}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        set(
                          "intakeMonths",
                          on ? draft.intakeMonths.filter((m) => m !== month) : [...draft.intakeMonths, month],
                        )
                      }
                      className={[
                        "min-h-8 rounded-full px-2.5 text-xs transition-colors",
                        on ? "bg-encre-800 text-white" : "bg-white text-encre-600 hover:bg-creme-200",
                      ].join(" ")}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <Field label="Rythme de l'alternance">
              <input {...text("rhythm")} className={inputClass} placeholder="2 j école / 3 j salon" />
            </Field>
            <Field label="OPCO">
              <input {...text("opco")} className={inputClass} placeholder="OPCO EP" />
            </Field>
            <div>
              <p className="mb-1.5 text-sm font-medium text-encre-700">Contrats proposés</p>
              <div className="flex flex-wrap gap-3 text-sm text-encre-700">
                {CONTRACTS.slice(0, 3).map((entry) => (
                  <label key={entry.key} className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={draft.contractTypes.includes(entry.key as never)}
                      onChange={(event) =>
                        set(
                          "contractTypes",
                          event.target.checked
                            ? [...draft.contractTypes, entry.key as "stage"]
                            : draft.contractTypes.filter((c) => c !== entry.key),
                        )
                      }
                    />
                    {entry.label}
                  </label>
                ))}
              </div>
            </div>
            <Field label="Élèves par an (environ)">
              <input {...text("studentsPerYear")} type="number" min={0} className={inputClass} />
            </Field>
          </div>
        </fieldset>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Convention : début">
          <input {...text("agreementStartsOn")} type="date" className={inputClass} />
        </Field>
        <Field label="Convention : fin">
          <input {...text("agreementEndsOn")} type="date" className={inputClass} />
        </Field>
        <Field label="Notes" className="sm:col-span-2">
          <textarea {...text("notes")} rows={3} className="w-full rounded-lg border border-creme-300 px-3 py-2" />
        </Field>
      </div>

      {save.isError && (
        <p role="alert" className="text-sm text-grenat-700">
          {save.error instanceof Error ? save.error.message : "Enregistrement impossible."}
        </p>
      )}

      <div className="flex gap-2">
        <Button type="submit" loading={save.isPending}>
          Enregistrer
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Annuler
        </Button>
      </div>
    </form>
  );
}

function Interactions({
  partnerId,
  status,
  interactions,
}: {
  partnerId: string;
  status: Status;
  interactions: PartnerDetail["interactions"];
}) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<(typeof INTERACTIONS)[number]["key"]>("call");
  const [occurredOn, setOccurredOn] = useState(todayIso);
  const [summary, setSummary] = useState("");
  const [followUpOn, setFollowUpOn] = useState("");
  const [nextStatus, setNextStatus] = useState<Status | "">("");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["partners"] });

  const add = useMutation({
    mutationFn: () =>
      api.admin.partners.addInteraction.mutate({
        partnerId,
        kind,
        occurredOn,
        summary,
        followUpOn: followUpOn || null,
        status: nextStatus || undefined,
      }),
    onSuccess: () => {
      setSummary("");
      setFollowUpOn("");
      setNextStatus("");
      invalidate();
    },
  });

  const toggle = useMutation({
    mutationFn: (input: { interactionId: string; done: boolean }) => api.admin.partners.completeFollowUp.mutate(input),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.admin.partners.deleteInteraction.mutate({ id }),
    onSuccess: invalidate,
  });

  return (
    <section className="mt-8">
      <h3 className="mb-2 text-lg">Échanges</h3>

      <form
        className="mb-4 space-y-3 rounded-xl bg-creme-50 p-4"
        onSubmit={(event) => {
          event.preventDefault();
          add.mutate();
        }}
      >
        <div className="flex flex-wrap gap-1.5">
          {INTERACTIONS.map((entry) => (
            <button
              key={entry.key}
              type="button"
              onClick={() => setKind(entry.key)}
              aria-pressed={kind === entry.key}
              className={[
                "min-h-9 rounded-full px-3 text-sm transition-colors",
                kind === entry.key ? "bg-blush-500 text-white" : "bg-white text-encre-700 hover:bg-creme-200",
              ].join(" ")}
            >
              {entry.glyph} {entry.label}
            </button>
          ))}
        </div>
        <textarea
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
          required
          rows={2}
          placeholder="Ce qui s'est dit, ce qui a été convenu…"
          aria-label="Résumé de l'échange"
          className="w-full rounded-lg border border-creme-300 bg-white px-3 py-2"
        />
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Le">
            <input type="date" value={occurredOn} onChange={(event) => setOccurredOn(event.target.value)} required className={inputClass} />
          </Field>
          <Field label="Relancer le">
            <div className="flex gap-1">
              <input type="date" value={followUpOn} onChange={(event) => setFollowUpOn(event.target.value)} className={inputClass} />
            </div>
          </Field>
          <Field label="Statut ensuite">
            <select value={nextStatus} onChange={(event) => setNextStatus(event.target.value as Status | "")} className={inputClass}>
              <option value="">Inchangé ({statusMeta(status).label})</option>
              {STATUSES.filter((entry) => entry.key !== status).map((entry) => (
                <option key={entry.key} value={entry.key}>
                  {entry.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-encre-400">Relance rapide :</span>
          {[
            { label: "dans 1 semaine", days: 7 },
            { label: "dans 1 mois", days: 30 },
            { label: "dans 3 mois", days: 90 },
          ].map((quick) => (
            <button
              key={quick.days}
              type="button"
              onClick={() => setFollowUpOn(inDays(quick.days))}
              className="rounded-full bg-white px-2.5 py-1 text-xs text-encre-600 hover:bg-creme-200"
            >
              {quick.label}
            </button>
          ))}
          <Button type="submit" size="sm" className="ml-auto" loading={add.isPending} disabled={!summary.trim()}>
            Ajouter au journal
          </Button>
        </div>
      </form>

      {interactions.length === 0 ? (
        <p className="text-sm text-encre-400">Aucun échange noté.</p>
      ) : (
        <ol className="space-y-3">
          {interactions.map((entry) => {
            const meta = INTERACTIONS.find((i) => i.key === entry.kind);
            const due = entry.followUpOn && !entry.followUpDoneAt;
            const late = due && entry.followUpOn! < todayIso();
            return (
              <li key={entry.id} className="rounded-lg border border-creme-200 px-3 py-2.5">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-xs text-encre-400">
                    {meta?.glyph} {meta?.label} · {formatDay(entry.occurredOn)}
                    {entry.author && ` · ${entry.author.firstName}`}
                  </p>
                  <button
                    type="button"
                    onClick={() => window.confirm("Supprimer cet échange ?") && remove.mutate(entry.id)}
                    className="text-xs text-encre-400 hover:text-grenat-700"
                  >
                    Supprimer
                  </button>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-encre-800">{entry.summary}</p>
                {entry.followUpOn && (
                  <label
                    className={[
                      "mt-2 inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs",
                      entry.followUpDoneAt
                        ? "bg-sauge-100 text-sauge-700"
                        : late
                          ? "bg-grenat-100 text-grenat-700"
                          : "bg-ambre-100 text-ambre-700",
                    ].join(" ")}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(entry.followUpDoneAt)}
                      onChange={(event) => toggle.mutate({ interactionId: entry.id, done: event.target.checked })}
                    />
                    {entry.followUpDoneAt ? "Relance faite" : `Relancer le ${formatDay(entry.followUpOn)}`}
                  </label>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

function Contacts({ partnerId, contacts }: { partnerId: string; contacts: PartnerDetail["contacts"] }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | "new" | null>(null);

  const remove = useMutation({
    mutationFn: (id: string) => api.admin.partners.deleteContact.mutate({ id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["partners"] }),
  });

  return (
    <section className="mt-8">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-lg">Contacts</h3>
        <Button size="sm" variant="secondary" onClick={() => setEditing("new")} disabled={editing === "new"}>
          + Contact
        </Button>
      </div>
      {editing === "new" && (
        <ContactForm partnerId={partnerId} onDone={() => setEditing(null)} />
      )}
      {contacts.length === 0 && editing !== "new" && (
        <p className="text-sm text-encre-400">Qui appeler ? Ajoutez la personne chargée des relations entreprises.</p>
      )}
      <ul className="space-y-2">
        {contacts.map((contact) =>
          editing === contact.id ? (
            <li key={contact.id}>
              <ContactForm partnerId={partnerId} contact={contact} onDone={() => setEditing(null)} />
            </li>
          ) : (
            <li key={contact.id} className="flex flex-wrap items-center gap-3 rounded-lg bg-creme-50 px-3 py-2 text-sm">
              <div className="min-w-0 flex-1">
                <p className="text-encre-800">
                  {contact.name}
                  {contact.isPrimary && (
                    <Badge tone="accent" className="ml-2">
                      Principal
                    </Badge>
                  )}
                </p>
                <p className="text-xs text-encre-500">
                  {[contact.role, contact.phone, contact.email].filter(Boolean).join(" · ")}
                </p>
              </div>
              {contact.phone && (
                <a href={`tel:${contact.phone}`} className="text-sm text-blush-600 hover:underline">
                  Appeler
                </a>
              )}
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="text-sm text-blush-600 hover:underline">
                  Écrire
                </a>
              )}
              <button type="button" onClick={() => setEditing(contact.id)} className="text-xs text-encre-500 hover:text-encre-800">
                Modifier
              </button>
              <button
                type="button"
                onClick={() => window.confirm(`Retirer ${contact.name} ?`) && remove.mutate(contact.id)}
                className="text-xs text-encre-400 hover:text-grenat-700"
              >
                Retirer
              </button>
            </li>
          ),
        )}
      </ul>
    </section>
  );
}

function ContactForm({
  partnerId,
  contact,
  onDone,
}: {
  partnerId: string;
  contact?: PartnerDetail["contacts"][number];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({
    name: contact?.name ?? "",
    role: contact?.role ?? "",
    email: contact?.email ?? "",
    phone: contact?.phone ?? "",
    isPrimary: contact?.isPrimary ?? false,
  });
  const save = useMutation({
    mutationFn: () => api.admin.partners.saveContact.mutate({ id: contact?.id, partnerId, ...draft }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      onDone();
    },
  });
  const set = (key: "name" | "role" | "email" | "phone") => (event: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((current) => ({ ...current, [key]: event.target.value }));

  return (
    <form
      className="mb-2 grid gap-2 rounded-lg bg-creme-50 p-3 sm:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <input value={draft.name} onChange={set("name")} required placeholder="Nom" aria-label="Nom" className={inputClass} />
      <input value={draft.role} onChange={set("role")} placeholder="Fonction" aria-label="Fonction" className={inputClass} />
      <input value={draft.phone} onChange={set("phone")} type="tel" placeholder="Téléphone" aria-label="Téléphone" className={inputClass} />
      <input value={draft.email} onChange={set("email")} type="email" placeholder="E-mail" aria-label="E-mail" className={inputClass} />
      <label className="flex items-center gap-2 text-sm text-encre-700">
        <input
          type="checkbox"
          checked={draft.isPrimary}
          onChange={(event) => setDraft((current) => ({ ...current, isPrimary: event.target.checked }))}
        />
        Contact principal
      </label>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Annuler
        </Button>
        <Button type="submit" size="sm" loading={save.isPending}>
          Enregistrer
        </Button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/*  Candidatures                                                               */
/* -------------------------------------------------------------------------- */

type CandidateDraft = {
  id?: string;
  partnerId: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  program: string;
  contractType: (typeof CONTRACTS)[number]["key"] | "";
  rhythm: string;
  desiredStartOn: string;
  status: CandidateStatus;
  portfolioUrl: string;
  notes: string;
  nextStepOn: string;
};

function emptyCandidate(): CandidateDraft {
  return {
    partnerId: null,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    program: "",
    contractType: "apprentissage",
    rhythm: "",
    desiredStartOn: "",
    status: "received",
    portfolioUrl: "",
    notes: "",
    nextStepOn: "",
  };
}

function toCandidateDraft(entry: {
  id: string;
  partnerId: string | null;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  program: string | null;
  contractType: string | null;
  rhythm: string | null;
  desiredStartOn: string | null;
  status: CandidateStatus;
  portfolioUrl: string | null;
  notes: string | null;
  nextStepOn: string | null;
}): CandidateDraft {
  return {
    id: entry.id,
    partnerId: entry.partnerId,
    firstName: entry.firstName,
    lastName: entry.lastName ?? "",
    email: entry.email ?? "",
    phone: entry.phone ?? "",
    program: entry.program ?? "",
    contractType: (entry.contractType as CandidateDraft["contractType"]) ?? "",
    rhythm: entry.rhythm ?? "",
    desiredStartOn: entry.desiredStartOn ?? "",
    status: entry.status,
    portfolioUrl: entry.portfolioUrl ?? "",
    notes: entry.notes ?? "",
    nextStepOn: entry.nextStepOn ?? "",
  };
}

function CandidateBoard({ onEdit }: { onEdit: (draft: CandidateDraft) => void }) {
  const queryClient = useQueryClient();
  const [showClosed, setShowClosed] = useState(false);
  const list = useQuery({
    queryKey: ["partners", "candidates"],
    queryFn: () => api.admin.partners.candidates.query({ salonId: SALON_ID }),
  });

  const move = useMutation({
    mutationFn: (input: { draft: CandidateDraft; status: CandidateStatus }) =>
      api.admin.partners.saveCandidate.mutate(toCandidateInput({ ...input.draft, status: input.status })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["partners"] }),
  });

  const all = list.data ?? [];
  const closed = all.filter((entry) => entry.status === "rejected" || entry.status === "withdrawn");

  if (list.data && all.length === 0) {
    return (
      <EmptyState
        glyph="💅"
        title="Aucune candidature"
        description="Ajoutez les CV reçus des écoles partenaires : vous suivrez chaque candidate jusqu'à la signature."
      />
    );
  }

  return (
    <section>
      <div className="grid gap-3 lg:grid-cols-5">
        {PIPELINE.map((step, index) => {
          const items = all.filter((entry) => entry.status === step.key);
          return (
            <div key={step.key} className="min-h-40 rounded-xl bg-creme-50 p-3">
              <p className="mb-2 flex items-center justify-between text-sm font-medium text-encre-700">
                {step.label}
                <span className="tabular-nums text-encre-400">{items.length}</span>
              </p>
              <ul className="space-y-2">
                {items.map((entry) => {
                  const draft = toCandidateDraft(entry);
                  const late = entry.nextStepOn && entry.nextStepOn < todayIso();
                  return (
                    <li key={entry.id} className="rounded-lg bg-white p-3 shadow-soft">
                      <button type="button" onClick={() => onEdit(draft)} className="block w-full text-left">
                        <p className="font-medium text-encre-900">
                          {entry.firstName} {entry.lastName}
                        </p>
                        {entry.program && <p className="text-xs text-encre-500">{entry.program}</p>}
                        <p className="mt-1 text-xs text-encre-400">
                          {[
                            entry.contractType && CONTRACTS.find((c) => c.key === entry.contractType)?.label,
                            entry.partnerName,
                            entry.desiredStartOn && `dès ${formatDay(entry.desiredStartOn)}`,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                        {entry.nextStepOn && (
                          <p className={["mt-1 text-xs", late ? "font-medium text-grenat-700" : "text-ambre-700"].join(" ")}>
                            Prochaine étape : {formatDay(entry.nextStepOn)}
                          </p>
                        )}
                      </button>
                      {/* Avancer d'une étape en un clic : le geste le plus fréquent. */}
                      <div className="mt-2 flex gap-1">
                        {index > 0 && (
                          <button
                            type="button"
                            aria-label="Étape précédente"
                            onClick={() => move.mutate({ draft, status: PIPELINE[index - 1]!.key })}
                            className="rounded-full bg-creme-100 px-2 text-xs text-encre-600 hover:bg-creme-200"
                          >
                            ←
                          </button>
                        )}
                        {index < PIPELINE.length - 1 && (
                          <button
                            type="button"
                            onClick={() => move.mutate({ draft, status: PIPELINE[index + 1]!.key })}
                            className="flex-1 rounded-full bg-creme-100 px-2 py-0.5 text-xs text-encre-700 hover:bg-creme-200"
                          >
                            {PIPELINE[index + 1]!.label} →
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      {closed.length > 0 && (
        <div className="mt-6">
          <button type="button" onClick={() => setShowClosed(!showClosed)} className="text-sm text-encre-500 hover:text-encre-800">
            {showClosed ? "▾" : "▸"} Candidatures closes ({closed.length})
          </button>
          {showClosed && (
            <ul className="mt-2 space-y-1.5">
              {closed.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    onClick={() => onEdit(toCandidateDraft(entry))}
                    className="flex w-full items-center justify-between rounded-lg bg-white px-3 py-2 text-left text-sm shadow-soft"
                  >
                    <span>
                      {entry.firstName} {entry.lastName}
                      {entry.partnerName && <span className="text-encre-400"> · {entry.partnerName}</span>}
                    </span>
                    <Badge tone="muted">{candidateLabel(entry.status)}</Badge>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function toCandidateInput(draft: CandidateDraft) {
  return {
    id: draft.id,
    salonId: SALON_ID,
    partnerId: draft.partnerId,
    firstName: draft.firstName,
    lastName: draft.lastName,
    email: draft.email,
    phone: draft.phone,
    program: draft.program,
    contractType: draft.contractType || null,
    rhythm: draft.rhythm,
    desiredStartOn: draft.desiredStartOn || null,
    status: draft.status,
    portfolioUrl: draft.portfolioUrl,
    notes: draft.notes,
    nextStepOn: draft.nextStepOn || null,
  };
}

function CandidateDialog({ draft: initial, onClose }: { draft: CandidateDraft; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(initial);
  const schools = useQuery({
    queryKey: ["partners", "list", "training_school-all"],
    queryFn: () => api.admin.partners.list.query({ salonId: SALON_ID, kind: "training_school" }),
  });

  const set = <K extends keyof CandidateDraft>(key: K, value: CandidateDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const text = (key: keyof CandidateDraft) => ({
    value: (draft[key] as string) ?? "",
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => set(key, event.target.value as never),
  });

  const done = () => {
    queryClient.invalidateQueries({ queryKey: ["partners"] });
    onClose();
  };
  const save = useMutation({ mutationFn: () => api.admin.partners.saveCandidate.mutate(toCandidateInput(draft)), onSuccess: done });
  const remove = useMutation({
    mutationFn: () => api.admin.partners.deleteCandidate.mutate({ id: draft.id! }),
    onSuccess: done,
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Candidature"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-encre-900/30 p-6 animate-fade-in"
      onClick={onClose}
    >
      <form
        className="max-h-full w-full max-w-xl overflow-y-auto rounded-xl bg-white p-6 shadow-lift animate-fade-up"
        onClick={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="text-2xl">{draft.id ? `${initial.firstName} ${initial.lastName}`.trim() : "Nouvelle candidature"}</h2>
          <button type="button" onClick={onClose} aria-label="Fermer" className="rounded-full px-2 text-encre-400 hover:bg-creme-100">
            ×
          </button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Prénom">
            <input {...text("firstName")} required maxLength={80} className={inputClass} />
          </Field>
          <Field label="Nom">
            <input {...text("lastName")} maxLength={80} className={inputClass} />
          </Field>
          <Field label="Téléphone">
            <input {...text("phone")} type="tel" className={inputClass} />
          </Field>
          <Field label="E-mail">
            <input {...text("email")} type="email" className={inputClass} />
          </Field>
          <Field label="École ou CFA">
            <select
              value={draft.partnerId ?? ""}
              onChange={(event) => set("partnerId", event.target.value || null)}
              className={inputClass}
            >
              <option value="">Candidature directe</option>
              {schools.data?.map((school) => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Formation">
            <input {...text("program")} className={inputClass} placeholder="CAP Esthétique, 1re année" />
          </Field>
          <Field label="Contrat">
            <select
              value={draft.contractType}
              onChange={(event) => set("contractType", event.target.value as CandidateDraft["contractType"])}
              className={inputClass}
            >
              <option value="">—</option>
              {CONTRACTS.map((entry) => (
                <option key={entry.key} value={entry.key}>
                  {entry.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Rythme">
            <input {...text("rhythm")} className={inputClass} placeholder="2 j école / 3 j salon" />
          </Field>
          <Field label="Début souhaité">
            <input {...text("desiredStartOn")} type="date" className={inputClass} />
          </Field>
          <Field label="Étape">
            <select value={draft.status} onChange={(event) => set("status", event.target.value as CandidateStatus)} className={inputClass}>
              {[...PIPELINE, ...CLOSED].map((entry) => (
                <option key={entry.key} value={entry.key}>
                  {entry.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Prochaine étape le">
            <input {...text("nextStepOn")} type="date" className={inputClass} />
          </Field>
          <Field label="CV ou portfolio (lien)">
            <input {...text("portfolioUrl")} type="url" className={inputClass} placeholder="https://instagram.com/…" />
          </Field>
          <Field label="Notes" className="sm:col-span-2">
            <textarea {...text("notes")} rows={3} className="w-full rounded-lg border border-creme-300 px-3 py-2" />
          </Field>
        </div>

        {(save.isError || remove.isError) && (
          <p role="alert" className="mt-3 text-sm text-grenat-700">
            {(save.error ?? remove.error) instanceof Error ? (save.error ?? remove.error)!.message : "Enregistrement impossible."}
          </p>
        )}

        <div className="mt-5 flex items-center gap-2">
          <Button type="submit" loading={save.isPending}>
            Enregistrer
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          {draft.id && (
            <Button
              type="button"
              variant="ghost"
              className="ml-auto text-grenat-700"
              loading={remove.isPending}
              onClick={() => window.confirm("Supprimer définitivement cette candidature ?") && remove.mutate()}
            >
              Supprimer
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

function Field({ label, className, children }: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <label className={["block text-sm", className].filter(Boolean).join(" ")}>
      <span className="mb-1 block font-medium text-encre-700">{label}</span>
      {children}
    </label>
  );
}
